import {
  forwardRef, MouseEventHandler, useCallback, useEffect, useState, useContext,
} from 'react'
import { getPurchaseOrderEOURL } from './links'
import { PurchaseOrderLine } from './api/purchaseorder'
import {ISODate} from './date'
import './POEditor.scss'
import {POAlmostPastDueWithoutShippingWarning}
  from './LinesWarning'
import {PurchaseOrderLinesTable} from './OrderLinesTable'

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useParams, useNavigate } from "react-router-dom";
import {api, useDivision} from "./shared"
import {AllDataContext} from "./LoggedinMainPage"


function is_are(count: number, singular: string, plural?: string): string {
  if (count === 1) {
    return `is 1 ${singular}`
  }
  return `are ${count} ${plural ?? (singular + "s")}`
}

export default function POEditor(){

  const allData = useContext(AllDataContext)
  const {shippingAggregate} = useParams<{shippingAggregate: string}>()
  const [useApi, setUseApi] = useState(true)
  const division = useDivision()
  const unfulfilledPurchaseOrderLines = allData.purchaseOrderLines.filter(
    pol => pol.receivedQuantity === 0)

  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [updateErrors, setUpdateErrors] = useState<readonly string[]>([])

  const navigate = useNavigate()

  const setSelectedShippingAggregatable =
    useCallback((shipping: string | undefined) => {
      if (!shipping) {
        navigate(`/division/${division}/POEditor`)
      } else {
        navigate(`/division/${division}/POEditor/`
                 + `shippingAggregate/${shipping}`)
      }
    }, [navigate, division])

  useEffect(() => {
    if (shippingAggregate === undefined) {
      return
    }
    const availableShippingAggregates = new Set<string>(
      unfulfilledPurchaseOrderLines.map(pol => pol.$calc.shipping)
      .reduce((a, b) => a.concat(b), [] as string[]))
    if (!availableShippingAggregates.has(shippingAggregate)) {
      setSelectedShippingAggregatable(undefined)
    }
  }, [shippingAggregate, unfulfilledPurchaseOrderLines,
    setSelectedShippingAggregatable])

  const updateReceiptDates = async (
      isodate: ISODate, pols: readonly PurchaseOrderLine[]) => {
    setUpdateErrors([])
    if (!useApi) {
      console.log("Printing out JS commands rather than updating")
      async function hackEOInterface(
          shipping: string,
          items: Map<string, readonly number[]>,
          newDate: string,
      ) {
        function sleep(time: number): Promise<undefined> {
          return new Promise((resolve) => {
            window.setTimeout(() => resolve(undefined), time)
          })
        }
        let nrlinesupdated = 0;
        const iframe = (document.getElementById("MainWindow") as HTMLIFrameElement)
        if (!iframe) {
          alert("You don't seem to be on the right page. If you are, please reload")
          return
        }
        const startSrc = iframe.src
        const iframeWindow = iframe.contentWindow!
        const getWaitForIframeLoadPromise = () => new Promise((resolve) => {
          const listener = (() => {
            iframe.removeEventListener("load", listener);
            resolve(undefined);
          })
          iframe.addEventListener("load", listener)
        })

        const loadIframeAndWait = (url: string) => new Promise((resolve) => {
            getWaitForIframeLoadPromise().then(resolve);
            iframe.src = url
        })
        for (const [url, linenrs] of items.entries()) {
          await loadIframeAndWait(url);
          for (const linenr of linenrs) {
            const note = iframeWindow.document.querySelector(
              `input[name="grd_r${linenr - 1}$Notes"]`) as HTMLInputElement
            if (note.value.indexOf(`shipping: ${shipping}`) === -1) {
              alert("Something is horribly wrong, it seems that the line I'm trying "
                    + "to update doesn't have the correct shipping: .... value....")
              return
            }
            const receiptDate = iframeWindow.document.querySelector(
              `input[name="grd_r${linenr - 1}$ReceiptDate"]`) as HTMLInputElement
            receiptDate.value = newDate;
            receiptDate.style.backgroundColor = "hsl(120, 80%, 60%)"
          }
          const saveButton =
            iframeWindow.document.querySelector(`#btnSave`) as HTMLButtonElement
          const loadPromise = getWaitForIframeLoadPromise()
          console.log("We need some time for the page to settle")
          await sleep(1000);
          saveButton.click();
          await loadPromise
          nrlinesupdated += linenrs.length;
        }
        await loadIframeAndWait(startSrc);
        alert(`Done, updated ${nrlinesupdated} lines in ${items.size} POs`)
      }

      const items = pols.reduce((items, pol) => {
        const urlstr =
          getPurchaseOrderEOURL(division, pol.parent.purchaseOrderID).toString()
        if (!items.has(urlstr)) {
          items.set(urlstr, new Array<number>())
        }
        items.get(urlstr)!.push(pol.lineNumber)
        return items
      }, new Map<string, number[]>())
    const code =
      hackEOInterface.toString()
      + "\n\n"
      + "hackEOInterface("
      + [
        JSON.stringify(shippingAggregate!),
        "new Map(" + JSON.stringify([...items.entries()]) + ")",
        JSON.stringify(isodate.toISOString())].join(", ")
      + ");\n"
      navigator.clipboard.writeText(code);
      alert("Code has been copied to clipboard. Now paste in the javascript console "
            + "on the Purchase Order overview page in Exact Online.\n\n"
            + "Warning: this method is possibly unsafe; always check after using it "
            + "that the right fields have been updated, to the right value !!!"
           )
      return
    }
    setIsUpdating(true)
    const errors = await PurchaseOrderLine.updateMultipleReceiptDates(
      api, division, isodate, pols)
    alert("Update complete, please reload this page")
    if (errors) {
      setUpdateErrors(errors)
    }
    setIsUpdating(false)
  }

  const polsByShippingAggregate = getPOLsByShippingAggregate(unfulfilledPurchaseOrderLines)

  const shippingAggregatableSelectDom =
    <div>
      Choose a Shipping label to view and adjust:
      <select value={shippingAggregate}
        className="shippingAggregate"
          data-cy="shippingAggregate"
          onChange={e => setSelectedShippingAggregatable(e.target.value)}>
        <option value=""> --- Please select Shipping aggregation --- </option>
        {Array.from(polsByShippingAggregate.entries())
          .sort(([_i, a]: [string, readonly PurchaseOrderLine[]],
                 [_j, b]: [string, readonly PurchaseOrderLine[]]
                ) => ISODate.compare(
            ISODate.earliest(a.map(pol => pol.receiptDate)),
            ISODate.earliest(b.map(pol => pol.receiptDate)),
          ))
          .map(([shipping, pols]: [string, readonly PurchaseOrderLine[]]) =>
            <option key={shipping} value={shipping}>
              {shipping} &mdash;
              {Array.from(new Set([...pols]
                                .sort((a, b) => ISODate.compare(
                                  a.receiptDate, b.receiptDate))
                                  .map(pol => pol.receiptDate.toDDMMYYYYString())))
              .join(", ")}
              &mdash;
              (po {pols.map(
                 pol => `${pol.parent.orderNumber}[${pol.lineNumber}]`).join(", ")}
              )
            </option>
          )
        }
      </select>
    </div>

  const pols = polsByShippingAggregate.get(shippingAggregate ?? "") ?? []

  const UpdateReceiptDateButtonDom = forwardRef<HTMLButtonElement>(
    (props, ref) => {
      const onClick = (props as {onClick: MouseEventHandler}).onClick
      return <button onClick={onClick} ref={ref}>Update Receipt Date</button>
    });

  return (
      <div className={isUpdating ? "updating" : "notupdating"}>
        <div id="updatingoverlay"><h1>Updating Purchase Orders</h1></div>
        <div className="notice">
          <details>
            <summary>
              Note: this overview only shows Purchase Order lines for which all
              of the following is true:
            </summary>
            <ul>
              <li>The Purchase Order is in state Open or Partial</li>
              <li>The Purchase Order is not part of a dropShipment</li>
              <li>The receivedQuantity is 0</li>
            </ul>
          </details>
        </div>
        <ul className="errorlist">
          {updateErrors.map((error, index) => <li key={index}>{error}</li>)}
        </ul>
        <POAlmostPastDueWithoutShippingWarning
          items={unfulfilledPurchaseOrderLines} />
        <div>{shippingAggregatableSelectDom}</div>
        {shippingAggregate && <>
          <h2>There {is_are(pols.length, "purchase order line")} with shipping
            information {shippingAggregate}</h2>
          <PurchaseOrderLinesTable
            items={pols} />
          <DatePicker
            selected={pols.length ? pols[0].receiptDate.toUTCNativeDate():
                      ISODate.localToday().toUTCNativeDate()}
            onChange={(date) => {
              if (!(date instanceof Date)) return;
              updateReceiptDates(ISODate.fromUTCNativeDate(date), pols)
            }}
            dateFormat="yyyy-MM-dd"
            monthsShown={2}
            disabledKeyboardNavigation
            showWeekNumbers
            customInput={<UpdateReceiptDateButtonDom />}
          />
        <input type="checkbox" checked={!useApi} id="useJsConsoleOutput"
          className="slider"
          onChange={e => setUseApi(!e.target.checked)} />
        <label htmlFor="useJsConsoleOutput">
          Advanced mode: don't use API to update, use javascript console commands.
        </label>
        </>
        }

    </div>
  )
}


function getPOLsByShippingAggregate(purchaseOrderLines: Readonly<PurchaseOrderLine[]>): Map<string, readonly PurchaseOrderLine[]> {
  const polsByShippingAggregate = new Map<string, readonly PurchaseOrderLine[]>()
  purchaseOrderLines
    .forEach(pol => {
      pol.$calc.shipping.forEach(shipping => {
        polsByShippingAggregate.set(shipping, [
          ...polsByShippingAggregate.get(shipping) ?? [],
          pol])
        if (!(polsByShippingAggregate.has(shipping))) {
        }
    })
  })
  return polsByShippingAggregate
}
